"use client";

import { useEffect, useMemo, useState } from "react";
import ProteinViewer from "@/components/ProteinViewer";

/* ================= TYPES ================= */

type ProteinStatus = {
  job_id: string;
  status: string;
  error?: string | null;
  sequence_length?: number;
  progress?: string | null;
};

type ProteinResult = {
  job_id: string;
  pdb_url?: string;
  plddt_mean?: number;
};

/* ================= PAGE ================= */

export default function ProteinPage() {
  const API = process.env.NEXT_PUBLIC_API_URL;

if (!API) {
  throw new Error("NEXT_PUBLIC_API_URL is not defined");
}

const BASE_API = API.replace(/\/$/, "");


  const [sequence, setSequence] = useState("");
  const [jobId, setJobId] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [result, setResult] = useState<ProteinResult | null>(null);

  /* ---------- PDB URL ---------- */
  const pdbUrl = useMemo(() => {
    if (!jobId || status !== "completed") return null;
    return `${API}/api/protein/download/${jobId}?file=pdb`;
  }, [jobId, status, API]);

  /* ================= SUBMIT ================= */

  const run = async () => {
    if (!sequence.trim()) {
      setError("Protein sequence is required.");
      return;
    }

    setError(null);
    setResult(null);
    setJobId(null);
    setStatus(null);
    setLoadingSubmit(true);

    try {
      const res = await fetch(`${API}/api/protein/run`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sequence }),
      });

      if (!res.ok) throw new Error(await res.text());

      const data = await res.json();
      setJobId(data.job_id);
      setStatus("queued");
    } catch (e: any) {
      setError(e.message || "Submission failed");
    } finally {
      setLoadingSubmit(false);
    }
  };

  /* ================= POLLING ================= */

  useEffect(() => {
    if (!jobId) return;

    let cancelled = false;

    const poll = async () => {
      try {
        const r = await fetch(`${API}/api/protein/status/${jobId}`);
        const data: ProteinStatus = await r.json();
        setStatus(data.status);

        if (data.status === "failed") {
          setError(data.error || "Job failed");
          return;
        }

        if (data.status !== "completed" && !cancelled) {
          setTimeout(poll, 2000);
          return;
        }

        const meta = await fetch(
          `${API}/api/protein/result/${jobId}`
        ).then((r) => r.json());

        if (!cancelled) setResult(meta);
      } catch (e: any) {
        if (!cancelled) {
          setError(e.message || "Failed to fetch results");
          setStatus("failed");
        }
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [jobId, API]);

  /* ================= UI ================= */

  return (
    <div
      className="
        min-h-screen
        bg-gradient-to-br from-black via-indigo-900 to-purple-900
        px-6 pt-20 pb-20
        text-white
      "
    >
      <div className="max-w-6xl mx-auto space-y-10">

        {/* ===== HEADER ===== */}
        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-8 shadow-2xl">
          <h1 className="text-3xl font-extrabold">
            Protein Structure Prediction (3D)
          </h1>
          <p className="mt-2 text-indigo-200 text-sm max-w-3xl">
            Predict and visualize protein tertiary structure directly from amino
            acid sequence using AI-powered folding models.
          </p>
        </div>

        {/* ===== MAIN CARD ===== */}
        <div
          className="
            bg-white/10 backdrop-blur-xl
            border border-white/20
            rounded-2xl p-8
            shadow-2xl
            grid md:grid-cols-2 gap-8
          "
        >
          {/* ===== INPUT PANEL ===== */}
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-indigo-200 mb-2">
                Protein sequence (FASTA or raw)
              </label>
              <textarea
                rows={10}
                value={sequence}
                onChange={(e) => setSequence(e.target.value)}
                placeholder=">MyProtein\nMGSSHHHHHHSSGLVP..."
                className="
                  w-full rounded-xl bg-black/40
                  border border-white/20
                  px-4 py-3 text-sm font-mono
                  focus:outline-none focus:ring-2 focus:ring-indigo-500
                "
              />
            </div>

            <button
              onClick={run}
              disabled={loadingSubmit || !sequence.trim()}
              className="
                w-full py-3 rounded-xl font-semibold
                bg-gradient-to-r from-emerald-500 to-cyan-500
                hover:scale-[1.02] transition
                disabled:opacity-40
              "
            >
              {loadingSubmit ? "Running…" : "Predict Protein Structure"}
            </button>

            {/* Status */}
            <div className="space-y-1 text-sm">
              {jobId && (
                <p className="text-indigo-200">
                  Job ID: <span className="font-mono">{jobId}</span>
                </p>
              )}
              {status && (
                <p>
                  Status:{" "}
                  <span className="font-semibold text-cyan-300">
                    {status}
                  </span>
                </p>
              )}
              {result?.plddt_mean !== undefined && (
                <p>
                  Mean pLDDT:{" "}
                  <strong className="text-emerald-400">
                    {result.plddt_mean.toFixed(1)}
                  </strong>
                </p>
              )}
              {error && (
                <p className="text-rose-400 font-medium">{error}</p>
              )}
            </div>
          </div>

          {/* ===== VIEWER ===== */}
          <div>
            <h3 className="text-sm font-semibold mb-3 text-indigo-200">
              3D Structure Viewer
            </h3>

            <div
              className="
                min-h-[600px] h-[600px] w-full
                rounded-xl overflow-hidden
                border border-white/20
                bg-black
              "
            >
              {pdbUrl ? (
                <ProteinViewer pdbUrl={pdbUrl} />
              ) : (
                <div className="h-full flex items-center justify-center text-gray-400">
                  Waiting for prediction…
                </div>
              )}
            </div>

            {pdbUrl && (
              <a
                href={pdbUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-block mt-3 text-xs text-cyan-400 underline"
              >
                ⬇ Download PDB file
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
